import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';

let appInsightsInstance = null;
let cachedConnectionString = null;

// Mutable bag of custom dimensions stamped on every telemetry item via the
// telemetry initializer. Updated over the app lifecycle (e.g. authMode).
const globalProperties = {};

/**
 * Returns the singleton Application Insights instance, initialising it on first
 * call. Returns `null` when no connection string is available (telemetry off).
 *
 * @param {string} [providedConnectionString] Optional connection string. When
 *   omitted, a previously cached string (see {@link initAppInsights}) is used.
 * @returns {ApplicationInsights | null}
 */
export function getAppInsights(providedConnectionString) {
    if (typeof window === 'undefined') return null;
    if (appInsightsInstance) return appInsightsInstance;

    const connectionString = providedConnectionString || cachedConnectionString;
    if (!connectionString) {
        return null;
    }
    cachedConnectionString = connectionString;

    const clickAnalyticsPlugin = new ClickAnalyticsPlugin();
    const clickAnalyticsConfig = {
        autoCapture: true,
        dataTags: {
            useDefaultContentNameOrId: true,
        },
    };

    const ai = new ApplicationInsights({
        config: {
            connectionString,
            /* ---- Page-view & route tracking ---- */
            // This app is state-driven (no client router), so route auto-tracking
            // has little to observe. Explicit PageViewed events are emitted from
            // AppInsightsProvider on view/appView changes instead.
            enableAutoRouteTracking: false,
            /* ---- Performance ---- */
            enableAjaxPerfTracking: true,
            maxAjaxCallsPerView: 50,
            /* ---- Exceptions ---- */
            enableUnhandledPromiseRejectionTracking: true,
            /* ---- Click analytics plugin ---- */
            extensions: [clickAnalyticsPlugin],
            extensionConfig: {
                [clickAnalyticsPlugin.identifier]: clickAnalyticsConfig,
            },
        },
    });

    ai.loadAppInsights();

    // Stamp the current global custom dimensions on every outgoing telemetry item.
    ai.addTelemetryInitializer((item) => {
        // Tag all questionnaire browser telemetry with a distinct cloud role. The
        // Application Insights resource is shared with the co-hosted client portal
        // (per environment), so this role lets downstream tooling separate
        // questionnaire browser telemetry from the portal's.
        item.tags = item.tags || {};
        item.tags['ai.cloud.role'] = 'xait-questionnaire-web';

        if (!item.data) return;
        const props = item.baseData?.properties ?? item.data.baseData?.properties ?? {};
        Object.keys(globalProperties).forEach((key) => {
            props[key] = globalProperties[key];
        });
        if (item.baseData) {
            item.baseData.properties = props;
        } else if (item.data.baseData) {
            item.data.baseData.properties = props;
        }
    });

    appInsightsInstance = ai;
    return ai;
}

/**
 * Initialise the SDK with a connection string (typically fetched at runtime from
 * the server's `/api/config`) and optional initial custom dimensions.
 *
 * @param {string} connectionString
 * @param {Record<string, string | number | boolean>} [initialProperties]
 * @returns {ApplicationInsights | null}
 */
export function initAppInsights(connectionString, initialProperties) {
    if (initialProperties) {
        setGlobalProperties(initialProperties);
    }
    return getAppInsights(connectionString);
}

/**
 * Merge custom dimensions into the global bag stamped on all telemetry.
 * @param {Record<string, string | number | boolean>} properties
 */
export function setGlobalProperties(properties) {
    if (!properties) return;
    Object.keys(properties).forEach((key) => {
        globalProperties[key] = String(properties[key]);
    });
}

/**
 * Track a custom event. No-ops gracefully when App Insights is not initialised.
 * @param {string} name
 * @param {Record<string, string | number | boolean>} [properties]
 */
export function trackEvent(name, properties) {
    getAppInsights()?.trackEvent({ name }, properties);
}

/**
 * Track an exception. Preferred over console.error for anything that should
 * appear in the App Insights Failures blade.
 * @param {Error} error
 * @param {Record<string, string>} [properties]
 */
export function trackException(error, properties) {
    getAppInsights()?.trackException({ exception: error }, properties);
}

/**
 * Track a custom metric (e.g. page dwell time).
 * @param {string} name
 * @param {number} average
 * @param {Record<string, string>} [properties]
 */
export function trackMetric(name, average, properties) {
    getAppInsights()?.trackMetric({ name, average }, properties);
}

/**
 * Set the authenticated user context. Call after login.
 * `userId` should be a stable, non-PII identifier (e.g. a hash of the email).
 * @param {string} userId
 * @param {string} [accountId]
 */
export function setAuthenticatedUser(userId, accountId) {
    getAppInsights()?.setAuthenticatedUserContext(userId, accountId, true);
}

/**
 * Clear the authenticated user context. Call on logout.
 */
export function clearAuthenticatedUser() {
    getAppInsights()?.clearAuthenticatedUserContext();
}

/**
 * Derive a stable, non-PII identifier from an email address. Never send raw
 * email as a user id or custom dimension.
 * @param {string} [email]
 * @returns {string}
 */
export function hashEmail(email) {
    if (!email) return 'unknown';
    try {
        const bytes = new TextEncoder().encode(email);
        const binary = Array.from(bytes, (byte) => String.fromCodePoint(byte)).join('');
        return btoa(binary).slice(0, 16);
    } catch {
        return 'unknown';
    }
}
